(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const deckMedia = matchMedia('(min-width: 1000px) and (min-height: 760px)');
  const instances = new Map();
  const motion = () => !reduced.matches && document.body.dataset.apexMotion !== 'false' && !window.Shopify?.designMode;
  function mount(root) {
    instances.get(root)?.();
    const controller = new AbortController();
    const {signal} = controller;
    const animations = new Set();
    const cleanups = [];
    const animate = (node, frames, duration = 550) => {
      if (!node || !motion()) return;
      const animation = node.animate(frames, {duration, easing:'cubic-bezier(.2,.7,.2,1)'});
      animations.add(animation);
      animation.finished.then(() => animations.delete(animation)).catch(() => {});
    };
    root.querySelectorAll('.apex-protocol--showcase').forEach(showcase => {
      const tabs = [...showcase.querySelectorAll('[data-protocol-tab]')];
      const panels = [...showcase.querySelectorAll('[data-protocol-panel]')];
      const select = (tab, focus = false, transition = true) => {
        if (!tab) return;
        const changed = tab.getAttribute('aria-selected') !== 'true';
        tabs.forEach(item => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        panels.forEach(panel => {
          const selected = panel.dataset.protocolPanel === tab.dataset.protocolTab;
          panel.hidden = !selected;
          if (!selected) return;
          const img = panel.querySelector('img');
          if (img) img.loading = 'eager';
          if (changed && transition) {
            animate(panel.querySelector('.apex-protocol__visual'), [{opacity:.65,clipPath:'inset(0 8% 0 0)'},{opacity:1,clipPath:'inset(0 0% 0 0)'}]);
            animate(panel.querySelector('.apex-protocol__footer'), [{opacity:.4,translate:'0 10px'},{opacity:1,translate:'0 0'}], 400);
          }
        });
        if (focus) tab.focus({preventScroll:true});
      };
      tabs.forEach((tab,index) => {
        tab.addEventListener('click', () => select(tab), {signal});
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
          if (event.key === 'ArrowLeft') next = tabs[(index + tabs.length - 1) % tabs.length];
          if (event.key === 'Home') next = tabs[0];
          if (event.key === 'End') next = tabs[tabs.length - 1];
          if (next) {event.preventDefault();select(next, true);}
        }, {signal});
      });
      select(tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || tabs[0], false, false);
    });
    root.querySelectorAll('.apex-stacks--deck[data-deck-motion="true"]').forEach(deck => {
      const cards = [...deck.querySelectorAll('.apex-stack')];
      let frame = 0, visible = false;
      const reset = () => cards.forEach(card => {card.style.removeProperty('--deck-scale');card.style.removeProperty('--deck-light');});
      const update = () => {
        frame = 0;
        if (!motion() || !deckMedia.matches || deck.dataset.deckFit !== 'true') {reset();return;}
        if (!visible) return;
        const boxes = cards.map(card => card.getBoundingClientRect());
        cards.forEach((card,index) => {
          const next = boxes[index + 1], box = boxes[index];
          const progress = next ? Math.max(0, Math.min(1, 1 - (next.top - box.top) / Math.max(box.height,1))) : 0;
          card.style.setProperty('--deck-scale', String(1 - progress * .025));
          card.style.setProperty('--deck-light', String(1 - progress * .13));
        });
      };
      const queue = () => {if (!frame) frame = requestAnimationFrame(update);};
      const bars = [...document.querySelectorAll('.shopify-section--header,.shopify-section--announcement-bar')];
      const measure = () => {
        const headerHeight = bars.reduce((height,bar) => ['sticky','fixed'].includes(getComputedStyle(bar).position) ? height + bar.getBoundingClientRect().height : height, 0);
        const step = parseFloat(getComputedStyle(deck).getPropertyValue('--deck-step')) || 0;
        const tallest = Math.max(0, ...cards.map(card => card.offsetHeight));
        deck.style.setProperty('--deck-header-height', `${Math.ceil(headerHeight)}px`);
        deck.dataset.deckFit = String(tallest + headerHeight + 44 + Math.max(0,cards.length - 1) * step <= innerHeight);
        queue();
      };
      const dimensions = new ResizeObserver(measure);
      [...bars,...cards].forEach(element => dimensions.observe(element));
      const observer = new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting);
        if (visible) queue();
      });
      observer.observe(deck);
      window.addEventListener('scroll', queue, {passive:true,signal});
      window.addEventListener('resize', measure, {passive:true,signal});
      reduced.addEventListener('change', queue, {signal});
      deckMedia.addEventListener('change', queue, {signal});
      measure();
      cleanups.push(() => {observer.disconnect();dimensions.disconnect();cancelAnimationFrame(frame);reset();deck.style.removeProperty('--deck-header-height');delete deck.dataset.deckFit;});
    });
    const cleanup = () => {controller.abort();animations.forEach(a=>a.cancel());cleanups.forEach(fn=>fn());};
    instances.set(root,cleanup);
  }
  const start = () => document.querySelectorAll('.shopify-section').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true}); else start();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {instances.get(event.target)?.();instances.delete(event.target);});
})();

if (!customElements.get('apex-system-cycle')) {
 class ApexSystemCycle extends HTMLElement {
  connectedCallback() {
   this.disconnect?.();
   const controller = new AbortController(), {signal} = controller;
   const panels = [...this.querySelectorAll('[data-cycle-panel]')];
   const captions = [...this.querySelectorAll('[data-cycle-caption]')];
   const buttons = [...this.querySelectorAll('[data-cycle-select]')];
   const toggle = this.querySelector('[data-cycle-toggle]');
   const status = this.querySelector('[data-cycle-status]');
   const reduced = matchMedia('(prefers-reduced-motion: reduce)');
   const hoverMedia = matchMedia('(hover:hover) and (pointer:fine)');
   const duration = Math.max(4,Number(this.dataset.duration) || 6) * 1000;
   const animations = new Set();
   let index = Math.max(0,panels.findIndex(panel => !panel.hidden));
   let elapsed = 0, last = 0, frame = 0, visible = false, hovering = false, touching = false, focused = false, paused = false;
   const allowMotion = () => !reduced.matches && document.body.dataset.apexMotion !== 'false';
   const autoEnabled = () => this.dataset.autoplay === 'true' && panels.length > 1 && allowMotion();
   const running = () => autoEnabled() && visible && !document.hidden && !hovering && !focused && !touching && !paused;
   const stop = () => {cancelAnimationFrame(frame);frame = 0;last = 0;};
   const animate = node => {
    if (!node || !allowMotion()) return;
    const animation = node.animate([{opacity:.25,transform:'scale(.975)'},{opacity:1,transform:'scale(1)'}],{duration:460,easing:'cubic-bezier(.2,.7,.2,1)'});
    animations.add(animation);
    animation.finished.then(() => animations.delete(animation)).catch(() => {});
   };
   const update = () => {
    if (signal.aborted) return;
    if (toggle) {
     toggle.hidden = !autoEnabled();
     toggle.dataset.paused = String(paused);
     toggle.setAttribute('aria-label',paused ? this.dataset.playLabel : this.dataset.pauseLabel);
    }
    const active = running();
    this.dataset.running = String(active);
    if (active && !frame) frame = requestAnimationFrame(tick);
    if (!active) stop();
   };
   const select = (next, announce = false, transition = true) => {
    if (!panels.length) return;
    const changed = index !== next;
    index = (next + panels.length) % panels.length;
    elapsed = 0;last = 0;this.style.setProperty('--cycle-progress','0');
    panels.forEach((panel,i) => {
     panel.hidden = i !== index;
     if (i === index) {
      const img = panel.querySelector('img');
      if (img) img.loading = 'eager';
      if (changed && transition) animate(panel);
     }
    });
    captions.forEach((caption,i) => caption.hidden = i !== index);
    buttons.forEach(button => button.setAttribute('aria-pressed',String(Number(button.dataset.cycleSelect) === index)));
    const active = buttons.find(button => Number(button.dataset.cycleSelect) === index);
    this.style.setProperty('--system-active',getComputedStyle(active).getPropertyValue('--phase-accent'));
    if (announce && status) status.textContent = captions[index]?.textContent.trim() || active?.textContent.trim();
    update();
   };
   const tick = now => {
    frame = 0;
    if (!running()) {update();return;}
    if (last) elapsed += Math.min(now - last,100);
    last = now;
    if (elapsed >= duration) select((index + 1) % panels.length);
    this.style.setProperty('--cycle-progress',String(Math.min(elapsed / duration,1)));
    if (!frame) frame = requestAnimationFrame(tick);
   };
   buttons.forEach(button => {
    button.disabled = false;
    button.addEventListener('click',() => select(Number(button.dataset.cycleSelect),true),{signal});
    button.addEventListener('keydown',event => {
     const current = Number(button.dataset.cycleSelect);
     let next;
     if (['ArrowRight','ArrowDown'].includes(event.key)) next = (current + 1) % panels.length;
     if (['ArrowLeft','ArrowUp'].includes(event.key)) next = (current + panels.length - 1) % panels.length;
     if (event.key === 'Home') next = 0;
     if (event.key === 'End') next = panels.length - 1;
     if (next === undefined) return;
     event.preventDefault();
     const group = button.closest('[role="group"]');
     group?.querySelector(`[data-cycle-select="${next}"]`)?.focus({preventScroll:true});
     select(next,true);
    },{signal});
   });
   toggle?.addEventListener('click',() => {paused = !paused;update();},{signal});
   this.addEventListener('pointerenter',event => {if (hoverMedia.matches && event.pointerType === 'mouse') {hovering = true;update();}},{signal});
   this.addEventListener('pointerleave',() => {hovering = false;update();},{signal});
   this.addEventListener('pointerdown',event => {if (event.pointerType !== 'mouse') {touching = true;update();}},{passive:true,signal});
   window.addEventListener('pointerup',() => {touching = false;update();},{passive:true,signal});
   window.addEventListener('pointercancel',() => {touching = false;update();},{passive:true,signal});
   this.addEventListener('focusin',() => {focused = true;update();},{signal});
   this.addEventListener('focusout',() => queueMicrotask(() => {focused = this.contains(document.activeElement);update();}),{signal});
   document.addEventListener('visibilitychange',update,{signal});
   reduced.addEventListener('change',() => {animations.forEach(animation => animation.cancel());animations.clear();update();},{signal});
   const motionObserver = new MutationObserver(update);
   motionObserver.observe(document.body,{attributes:true,attributeFilter:['data-apex-motion']});
   const observer = new IntersectionObserver(entries => {
    visible = entries.some(entry => entry.isIntersecting);
    if (visible) this.querySelectorAll('.apex-system__image').forEach(image => {if (image.tagName === 'IMG') image.loading = 'eager';});
    update();
   },{threshold:0});
   observer.observe(this);
   this.addEventListener('shopify:block:select',event => {
    const button = event.target.closest('[data-cycle-select]');
    if (button) {paused = true;select(Number(button.dataset.cycleSelect),false,false);}
   },{signal});
   this.dataset.ready = 'true';
   select(index,false,false);
   this.disconnect = () => {controller.abort();observer.disconnect();motionObserver.disconnect();stop();animations.forEach(animation => animation.cancel());this.dataset.running = 'false';};
  }
  disconnectedCallback() {this.disconnect?.();}
 }
 customElements.define('apex-system-cycle',ApexSystemCycle);
}
